import { RoundedFilledButton } from '@/components/buttons/Buttons';
import Dialog from '@/components/dialog/Dialog';
import DialogHeader from '@/components/dialog/DialogHeader';
import FileUploadInput from '@/components/file-upload/FileUploadInput';
import { type Dispatch, type SetStateAction, useState } from 'react';
import bulkAddModule from './bulk-add.module.css';
import { BulkProcessRequest, FileUploadControl, UploadEmployeesResponse } from '@/components/form-inputs/FileUpload4Form';
import { ENDPOINTS } from '@/constants/endpoints';
import { showError, showNotification } from '@/modules/notification/engine/notification.library';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse } from '@/common/common_utils';
import { People } from '@/modules/people/engine/People';

type Props = {
	visibleBulk: boolean;
	setVisibleBulk: Dispatch<SetStateAction<boolean>>;
};

const DIALOG_STATE = {
	ACTIVE: 'ACTIVE',
	UPLOADING: 'UPLOADING',
	ERROR: 'ERROR',
	SUCCESS: 'SUCCESS',
} as const;

type DialogState = (typeof DIALOG_STATE)[keyof typeof DIALOG_STATE];

const BulkAddDialog = ({ visibleBulk, setVisibleBulk }: Props) => {
	const [dialogState, setDialogState] = useState<DialogState>(
		DIALOG_STATE.ACTIVE,
	);
	const [fileUrl, setFileUrl] = useState<string | undefined>();

	const sendInvites = async () => {
		await People.sendInvites(fileUrl!, 'add-people');
		setVisibleBulk(false);
	}

	return (
		<Dialog
			header={<DialogHeader title="Bulk Add" />}
			visible={visibleBulk}
			onHide={() => {
				if (!visibleBulk) return;
				setVisibleBulk(false);
			}}
			className="custom-employee-dialog tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)]"
			pt={{
				content: () => ({
					className: bulkAddModule.content,
				}),
			}}
		>
			<p>
				Invite your team to join your company's account. For a faster
				activation experience, add a phone number. This allows Xpress to
				send the invitation directly through the app and/or by SMS if
				the phone number is associated with an Xpress account.
			</p>
			<p>
				Importing a CSV allows you to add and update people in bulk.
				To get started{' '}
				<button
					type="button"
					onClick={async () => {
						showNotification('Downloading template');
						const response = await axiosInstance.post(
							ENDPOINTS['/api/X4bEmployee/DownloadCsvTemplate'],
							{},
							{ responseType: 'blob' }
						);
						// Create a blob from the response
						const blob = new Blob([response.data], { type: 'text/csv' });
						// Create a link element
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', 'invite-employees.csv');
						document.body.appendChild(link);
						link.click();
						link.parentNode?.removeChild(link);
						window.URL.revokeObjectURL(url);
					}}
					className="text-tertiary underline"
				>
					Download the Template.
				</button>
			</p>
			<p>
				We'll add people to the group specified in the CSV. If you don't
				specify a group, well add them to General
			</p>

			<FileUploadControl
				value={fileUrl}
				onChange={setFileUrl}
				uploadEndpoint={ENDPOINTS['/api/X4bFile/UploadFile']}
				accept='.csv,text/csv'
				title='Drop files here to upload'
			></FileUploadControl>
			<RoundedFilledButton
				id="bulk-add-send"
				label="Send Invite"
				className={bulkAddModule.sendButton}
				disabled={!fileUrl}
				onClick={() => {
					sendInvites();
				}}
			/>
		</Dialog>
	);
};

export default BulkAddDialog;
